import { isNullOrWhitespace } from "../../../framework/utils";
import { forEach } from "../../../framework/utils/collection";

export enum MarkdownComponent {
  Unknown,
  Continuation,
  Heading,
  Table,
  TableFragment,
  Blockquote,
  BlockquoteFragment,
  OrderedList,
  OrderedListFragment,
  UnorderedList,
  UnorderedListFragment,
  Paragraph,
  TimingSpec,
  ThickRule,
  ThinRule,
}

export interface IMarkdownLine {
  type: MarkdownComponent;
  lineNumber: number;
  content: string;
}

export interface IComponentSource {
  type: MarkdownComponent;
  startLineNumber: number;
  endLineNumber: number;
  content: IMarkdownLine[];
}

export class ComponentSource implements IComponentSource {
  constructor(public type: MarkdownComponent = MarkdownComponent.Unknown) {}
  public startLineNumber: number = 0;
  public endLineNumber: number = 0;
  public content: IMarkdownLine[] = [];
}

export class ComponentModel implements IComponentSource {
  constructor (source: IComponentSource) {
    this.type = source.type;
    this.startLineNumber = source.startLineNumber;
    this.endLineNumber = source.endLineNumber;
    this.content = source.content;
  }

  public type: MarkdownComponent;
  public startLineNumber: number;
  public endLineNumber: number;
  public content: IMarkdownLine[];
}

export const headingPattern = /^\s*#{1,6}\s+/;
export const timingSpecPattern = /^\s*[#$]([0-9]+(:[0-9]+)?|:[0-9]+)\s+/;
export const formulaicTimingSpecPattern = /\s*[#$]([0-9]+(?::[0-9]+)?|:[0-9]+)\s*([-+/*%])\s*([0-9]+(?::[0-9]+)?|:[0-9]+)\s+/;
const tablePattern = /^\s*\|.*\|\s*$/;
export const orderedListPattern = /^\s*[0-9]+[.)]\s+/;
export const unorderedListPattern = /^\s*[-+*]\s+/;
export const blockquotePattern = /(?:^\s*>\s+)|(?:^\s*>$)/;
export const thickRulePattern = /^\s*=+\s*$/;
export const thinRulePattern = /^\s*-+\s*$/;
export const strictUrlPattern = /^(?:[a-z]+:\/\/)?(?:[a-z0-9-]+(?:\.[a-z0-9-]+)*)(?::[0-9]+)?(?:\/(?:(?:%[0-9a-f]{2})?[a-z0-9-._~!$&'()*+,;=:@]*)*)*(?:\?(?:(?:%[0-9a-f]{2})?[a-z0-9-._~!$&'()*+,;=:@/?]*)*)?(?:#(?:(?:%[0-9a-f]{2})?[a-z0-9-._~!$&'()*+,;=:@/?]*)*)?$/i;
const determineComponentType = (line: string, previousComponent: MarkdownComponent, notAFragment: boolean): MarkdownComponent => {
  if(headingPattern.test(line)) {
    return MarkdownComponent.Heading;
  }
  if(formulaicTimingSpecPattern.test(line)) {
    return MarkdownComponent.TimingSpec;
  }
  if(timingSpecPattern.test(line)) {
    return MarkdownComponent.TimingSpec;
  }
  if(tablePattern.test(line)) {
    return notAFragment || previousComponent !== MarkdownComponent.Table ? MarkdownComponent.Table : MarkdownComponent.TableFragment;
  }
  if(orderedListPattern.test(line)) {
    return notAFragment || (previousComponent !== MarkdownComponent.OrderedList && previousComponent !== MarkdownComponent.UnorderedList) ?
      MarkdownComponent.OrderedList : MarkdownComponent.OrderedListFragment;
  }
  if(unorderedListPattern.test(line)) {
    return notAFragment || (previousComponent !== MarkdownComponent.OrderedList && previousComponent !== MarkdownComponent.UnorderedList) ?
      MarkdownComponent.UnorderedList : MarkdownComponent.UnorderedListFragment;
  }
  if(blockquotePattern.test(line)) {
    return notAFragment || previousComponent !== MarkdownComponent.Blockquote ?
      MarkdownComponent.Blockquote : MarkdownComponent.BlockquoteFragment;
  }

  // these patterns can only be considered as components if preceded by an empty line
  if(notAFragment) {
    if(thickRulePattern.test(line)) { return MarkdownComponent.ThickRule; }
    if(thinRulePattern.test(line)) { return MarkdownComponent.ThinRule; }
    return MarkdownComponent.Paragraph;
  }

  return MarkdownComponent.Continuation;
}

const isFragmentType = (type: MarkdownComponent) => {
  return type === MarkdownComponent.BlockquoteFragment
    || type === MarkdownComponent.OrderedListFragment
    || type === MarkdownComponent.TableFragment
    || type === MarkdownComponent.UnorderedListFragment;
}

export const getComponentId = (source: IComponentSource) => `${source.startLineNumber}-${source.endLineNumber}`;

export const getMarkdownLines = (markdown: string) => {
  const textLines = markdown.split('\n');

  const mdLines: IMarkdownLine[] = [];
  let notAFragment = true;
  let prevComponent = MarkdownComponent.Unknown;
  forEach(textLines, (line, index) => {
    if(isNullOrWhitespace(line)) {
      notAFragment = true;
    } else {
      const mdl: IMarkdownLine = {
        type: determineComponentType(line, prevComponent, notAFragment),
        lineNumber: index! + 1,
        content: line
      };

      mdLines.push(mdl);
      prevComponent = mdl.type !== MarkdownComponent.Continuation && isFragmentType(mdl.type) === false ? mdl.type : prevComponent;
      notAFragment = false;
    }
  });

  return mdLines;
}

export const compressComponentSource = (source: IComponentSource, breakNewLines: boolean) => {
  const compressedContent: IMarkdownLine[] = [];
  const joinToken = breakNewLines ? '<br/>' : ' ';
  let currentLine: IMarkdownLine | null = null;
  forEach(source.content, line => {
    // distinct line
    if(line.type !== MarkdownComponent.Continuation) {
      compressedContent.push(line);
      currentLine = line;
    } else {
      currentLine!.content = `${currentLine!.content}${joinToken}${line.content}`;
    }

  });

  source.content = compressedContent;
}

export const distillComponentSource = (lines: IMarkdownLine[]) => {
  const source: IComponentSource[] = [];
  let currentSource: IComponentSource | null = null;
  forEach(lines, line => {
    // new component
    if(isFragmentType(line.type) === false && line.type !== MarkdownComponent.Continuation) {
      if(!!currentSource) {
        currentSource.endLineNumber = currentSource.content[currentSource.content.length - 1].lineNumber;
        source.push(currentSource);
      }
      const cs = new ComponentSource(line.type);
      cs.startLineNumber = !!currentSource ? currentSource.endLineNumber + 1 : 1;
      currentSource = cs;
    }

    currentSource!.content.push(line);
  });

  if(!!currentSource) {
    const cs = currentSource as IComponentSource;
    cs.endLineNumber = cs.content[cs.content.length - 1].lineNumber;
    source.push(cs);
  }

  return source;
}

export const getIndentationCount = (line: string) => {
  const leadingSpace = line.match(/^\s*/)![0];
  const tabIndents = leadingSpace.match(/\t/g)?.length ?? 0;
  const spaceIndents = Math.floor((leadingSpace.length - tabIndents) / 4);
  return tabIndents + spaceIndents + 1;
}

export function peek<T>(array: T[]): T | null {
  return array.length > 0 ? array[array.length - 1] : null;
}